import { ChessGame, chooseBotMove } from './engine.js';
import { XiangqiGame, chooseXiangqiBotMove } from './xiangqi-engine.js';
import { chooseCaroBotMove } from './caro-engine.js';
import { PROTOCOL_VERSION, MSG_TYPES, ROOM_STATUS, isValidRoomCode, normalizeRoomCode } from './shared/protocol.js';
import { CONFIG } from './config.js';
import { CHESS_SVG } from './chess-svgs.js';
import { getPieceGuide } from './piece-guides.js';
import { createLocalDrawResult, createLocalResignResult } from './local-game-result.js';

import { XQ_PIECES, DIFFICULTY, GAME_CATALOG } from './src/core/catalog.js';
import { queryOne, queryAll } from './src/core/dom.js';
import { getDeviceId } from './src/core/device.js';
import { PlayerIdentityClient, avatarFor } from './src/core/player-identity.js';
import {
  pieceColorForGame,
  sideNameForGame,
  createGameEngine
} from './src/core/game-adapter.js';
import {
  saveLocalGameSnapshot,
  loadLocalGameSnapshot,
  isLocalGameFinished
} from './src/core/local-game-store.js';
import { createRequestId } from './src/realtime/transport.js';
import { RealtimeRoomController } from './src/realtime/room-controller.js';
import { renderXiangqiBoard } from './src/games/xiangqi/view.js';
import { renderChessBoard } from './src/games/chess/view.js';
import { renderCaroBoard, renderCaroPosition } from './src/games/caro/view.js';
import { TycoonController } from './src/games/tycoon/controller.js';
import { TycoonAvatarCoordinator } from './src/games/tycoon/avatar-coordinator.js';
import { renderTycoonLobby } from './src/games/tycoon/lobby.js';
import {
  normalizeSelectedAsset,
  renderTycoonBoard as renderTycoonBoardView,
  renderTycoonPlayers as renderTycoonPlayersView,
  renderTycoonManagement as renderTycoonManagementView,
  renderTycoonLog as renderTycoonLogView,
  renderTycoonHud,
  showTycoonAssetInspector
} from './src/games/tycoon/view.js';
import { activateView, renderModeCapabilities } from './src/ui/navigation.js';
import { renderConnectionStatus, deriveOnlineGameStatus } from './src/ui/status.js';
import { RoomDirectoryController } from './src/ui/room-directory.js';
import { LiveRoomActionsController } from './src/ui/live-room-actions.js';
import { gameDecisionCopy, leaveRoomCopy } from './src/ui/dialogs.js';
import {
  renderMoveHistory,
  renderCapturedPieces as renderCapturedSidebar
} from './src/ui/game-sidebar.js';
import { initTheme } from './src/ui/theme.js';
import { PlayerProfileController } from './src/ui/player-profile.js';
import { CompetitiveSocialController } from './src/ui/competitive-social.js';

const deviceId = getDeviceId();
const playerIdentity = new PlayerIdentityClient({ baseUrl: CONFIG.LIVE_BACKEND_URL });

// App State
let currentGameType = null; // 'chess' | 'xiangqi' | 'caro' | 'tycoon'
let currentMode = 'bot';    // 'bot' | 'local' | 'online'
let localGame = null;
let tycoonGame = null;
let selectedTycoonAsset = null;
let localManualResult = null;
let selectedSquare = null;
let legalMoves = [];
let boardFlipped = false;
let botThinking = false;
let botWorker = null;
let botRequestSequence = 0;
let isFullscreen = false;

let onlineState = null;
let onlineUserSide = null; // 'w' | 'b' | null (spectator)
let onlineUserSeat = null; // 0..3 for Tycoon Live
let onlineRole = 'lobby';
let currentRoomCode = '';
let currentRoomPassword = '';
let currentHostToken = '';
let eventToastTimer = null;
let onlinePendingRequestId = null;
let onlinePendingRevision = null;
let pendingLeaveDestination = null;
let leaveAckTimer = null;
let pendingGameDecision = null;
let playerProfileController = null;
let competitiveSocialController = null;

const boardEl = queryOne('#board');
const statusEl = queryOne('#gameStatus');
const turnSubtextEl = queryOne('#turnSubtext');
const historyEl = queryOne('#history');
const moveCountEl = queryOne('#moveCount');
const modeBadgeEl = queryOne('#modeBadge');
const onlineIndicatorEl = queryOne('#onlineIndicator');
const onlineStatusTextEl = queryOne('#onlineStatusText');
const botControlsCard = queryOne('#botControlsCard');
const undoBtn = queryOne('#undoBtn');
const flipBtn = queryOne('#flipBtn');
const newGameBtn = queryOne('#newGameBtn');
const rematchBtn = queryOne('#rematchBtn');
const leaveRoomBtn = queryOne('#leaveRoomBtn');
const liveDecisionActions = queryOne('#liveDecisionActions');
const drawBtn = queryOne('#drawBtn');
const resignBtn = queryOne('#resignBtn');
const declineDrawBtn = queryOne('#declineDrawBtn');
const gameDecisionDialog = queryOne('#gameDecisionDialog');
const gameDecisionIcon = queryOne('#gameDecisionIcon');
const gameDecisionTitle = queryOne('#gameDecisionTitle');
const gameDecisionMessage = queryOne('#gameDecisionMessage');
const confirmGameDecisionBtn = queryOne('#confirmGameDecisionBtn');
const cancelGameDecisionBtn = queryOne('#cancelGameDecisionBtn');
const leaveRoomDialog = queryOne('#leaveRoomDialog');
const leaveRoomMessageEl = queryOne('#leaveRoomMessage');
const confirmLeaveRoomBtn = queryOne('#confirmLeaveRoomBtn');
const cancelLeaveRoomBtn = queryOne('#cancelLeaveRoomBtn');
const promoDialog = queryOne('#promotionDialog');
const roomLobbyErrorEl = queryOne('#roomLobbyError');
const eventToastEl = queryOne('#eventToast');

const tycoonController = new TycoonController();
const tycoonAvatarCoordinator = new TycoonAvatarCoordinator({
  controller: tycoonController,
  deviceId,
  context: () => ({ game: tycoonGame, mode: currentMode, state: onlineState, seat: onlineUserSeat }),
  send: sendWsMessage,
  persistLocal: () => { saveTycoonGame(); renderTycoon(); },
  renderGame: renderTycoon,
  renderLobby: updateTycoonSeatPickerUI
});

let homeRoomDirectoryController = null;
let roomActionsController = null;
const roomDirectoryController = new RoomDirectoryController({
  roomsUrl: CONFIG.getRoomsUrl(),
  root: '#lobbyRoomDirectory',
  onEnterRoom: room => enterRoomFromDirectory(room),
  onChangeGame: room => roomActionsController?.openChangeGame(room),
  onNotice: message => { roomLobbyErrorEl.textContent = message; },
  onMutated: () => { void homeRoomDirectoryController?.refresh({ quiet: true }); }
});
roomDirectoryController.mount();

homeRoomDirectoryController = new RoomDirectoryController({
  roomsUrl: CONFIG.getRoomsUrl(),
  root: '#homeRoomDirectory',
  selectors: {
    gameFilter: '#homeRoomGameFilter',
    statusFilter: '#homeRoomStatusFilter',
    refresh: '#homeRefreshRoomsBtn',
    list: '#homeRoomDirectoryList',
    count: '#homeRoomDirectoryCount'
  },
  defaultGame: '',
  limit: 24,
  onEnterRoom: room => enterRoomFromDirectory(room),
  onChangeGame: room => roomActionsController?.openChangeGame(room),
  onNotice: message => {
    const notice = queryOne('#homeRoomDirectoryNotice');
    if (notice) notice.textContent = message;
  },
  onMutated: () => { void roomDirectoryController.refresh({ quiet: true }); }
});
homeRoomDirectoryController.mount();

const roomController = new RealtimeRoomController({
  onStatus: status => updateOnlineConnectionStatus(status),
  onMessage: message => handleServerMessage(message),
  onError: message => showConnectionError(message),
  onReconnectNeeded: () => {
    if (currentMode === 'online' && currentRoomCode) connectWebSocket();
  },
  pingMessage: { type: MSG_TYPES.PING, protocolVersion: PROTOCOL_VERSION }
});

roomActionsController = new LiveRoomActionsController({
  roomsUrl: CONFIG.getRoomsUrl(),
  getPlayerToken: async () => {
    try {
      await playerIdentity.ensureGuest();
      playerProfileController?.render();
      return playerIdentity.token;
    } catch {
      return '';
    }
  },
  getCurrentRoom: () => ({
    gameType: currentGameType,
    roomCode: currentRoomCode,
    password: currentRoomPassword,
    hostToken: currentHostToken,
    isHost: onlineState?.room?.isHost === true,
    status: onlineState?.status || 'waiting',
    allowSpectators: onlineState?.room?.allowSpectators ?? true
  }),
  onActivateRoom: payload => activateCreatedRoom(payload),
  onDisconnectCurrentRoom: () => {
    roomController.suppressReconnect();
    disconnectWebSocket();
  },
  onRefresh: () => {
    void homeRoomDirectoryController?.refresh({ quiet: true });
    void roomDirectoryController.refresh({ quiet: true });
  },
  onNotice: message => { roomLobbyErrorEl.textContent = message; }
});
roomActionsController.mount();

competitiveSocialController = new CompetitiveSocialController({
  identity: playerIdentity,
  baseUrl: CONFIG.LIVE_BACKEND_URL,
  getRoomContext: () => ({
    gameType: currentGameType,
    roomCode: currentRoomCode,
    status: onlineState?.status || null,
    hasPassword: onlineState?.room?.hasPassword === true
  })
});
competitiveSocialController.mount();

playerProfileController = new PlayerProfileController({
  identity: playerIdentity,
  baseUrl: CONFIG.LIVE_BACKEND_URL,
  canChangeIdentity: () => !(currentMode === 'online' && currentRoomCode),
  onProfileChanged: () => {
    if (queryOne('#communityDialog')?.open) void competitiveSocialController?.refresh();
  }
});
void playerProfileController.mount();

const guideTitleEl = queryOne('#guideTitle');
const guideTextEl = queryOne('#guideText');
const guideIconEl = queryOne('#guideIcon');

const fullscreenHud = queryOne('#fullscreenHud');
const fsStatusDot = queryOne('#fsStatusDot');
const fsStatusText = queryOne('#fsStatusText');

const currentColor = piece => pieceColorForGame(currentGameType, piece);
const sideName = color => sideNameForGame(currentGameType, color);
const createLocalEngine = () => createGameEngine(currentGameType);
function showMoveNotice(move, status) {
  if (!move || (!move.captured && !status?.check)) return;
  clearTimeout(eventToastTimer);
  const icon = queryOne('#eventToastIcon');
  const text = queryOne('#eventToastText');
  icon.innerHTML = '';
  eventToastEl.classList.toggle('check-toast', Boolean(status?.check));

  if (move.captured) {
    if (currentGameType === 'chess') {
      icon.innerHTML = CHESS_SVG[move.captured] || '⚔️';
    } else {
      const pieceIcon = document.createElement('span');
      pieceIcon.className = `xq-piece ${currentColor(move.captured) === 'w' ? 'red' : 'black'}`;
      pieceIcon.textContent = XQ_PIECES[move.captured] || '⚔';
      icon.appendChild(pieceIcon);
    }
    const capturedName = (getPieceGuide(currentGameType, move.captured)?.name || 'quân cờ').split(' (')[0];
    const mover = currentColor(move.piece);
    text.textContent = `${mover ? sideName(mover) : 'Đối thủ'} vừa ăn ${capturedName}${status?.check ? ' — Chiếu!' : ''}`;
  } else {
    icon.textContent = '⚠️';
    text.textContent = `Chiếu! ${sideName(status.turn)} cần bảo vệ ${currentGameType === 'chess' ? 'Vua' : 'Tướng'}.`;
  }

  eventToastEl.classList.remove('hidden');
  eventToastTimer = setTimeout(() => eventToastEl.classList.add('hidden'), 3200);
}

function updateCheckPresentation(inCheck) {
  queryOne('#boardWrap').classList.toggle('board-in-check', Boolean(inCheck));
  queryOne('#statusCard').classList.toggle('check-alert', Boolean(inCheck));
}

// Local Storage for Offline Games
function saveLocalGame() {
  saveLocalGameSnapshot({
    gameType: currentGameType,
    mode: currentMode,
    game: localGame,
    result: localManualResult
  });
}

function loadLocalGame() {
  const snapshot = loadLocalGameSnapshot({
    gameType: currentGameType,
    mode: currentMode,
    createEngine: createGameEngine
  });
  localManualResult = snapshot.result;
  return snapshot.game;
}

function localEngineFinished(status = localGame?.status()) {
  return isLocalGameFinished(status, localManualResult);
}
function updateLocalDecisionControls(status = localGame?.status()) {
  if (currentMode === 'online') return;
  const active = Boolean(localGame && !localEngineFinished(status));

  liveDecisionActions.classList.toggle('hidden', !active);
  resignBtn.disabled = !active;
  drawBtn.disabled = !active;
  declineDrawBtn.classList.add('hidden');

  if (active) {
    drawBtn.textContent = '½ Chọn hòa';
    drawBtn.onclick = () => openGameDecision('draw');
  }

  undoBtn.disabled = !active;
}

// Update Piece Guide Box
function updatePieceGuide(pieceChar = null) {
  if (!pieceChar) {
    guideIconEl.textContent = '💡';
    guideTitleEl.textContent = 'Hướng dẫn quân cờ';
    guideTextEl.textContent = currentGameType === 'chess'
      ? 'Chạm vào một quân cờ để xem cách đi, ăn quân và các ô hợp lệ trên bàn cờ.'
      : currentGameType === 'xiangqi'
        ? 'Chạm vào quân cờ để xem luật đi (sông, cung tướng, cản chân Mã, cản mắt Tượng, Pháo).'
        : 'Chạm một ô trống để đặt quân. X đi trước; bên đầu tiên tạo 5 quân liên tiếp thắng.';
    return;
  }

  const info = getPieceGuide(currentGameType, pieceChar);
  if (info) {
    guideIconEl.textContent = currentGameType === 'chess' ? '♟' : '🀄';
    guideTitleEl.textContent = info.name;
    guideTextEl.textContent = info.rule;
  }
}

// Fullscreen Management
function toggleFullscreen(force = null) {
  const willBeFs = force !== null ? force : !isFullscreen;
  isFullscreen = willBeFs;

  document.body.classList.toggle('fullscreen-active', isFullscreen);
  fullscreenHud.classList.toggle('hidden', !isFullscreen);
  queryOne('#fullscreenBtn').textContent = isFullscreen ? '🗗' : '⛶';

  if (isFullscreen) {
    if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  } else {
    if (document.exitFullscreen && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  }

  updateFullscreenHud();
}

function updateFullscreenHud() {
  if (!isFullscreen) return;

  if (currentMode !== 'online' && localManualResult) {
    fsStatusText.textContent = localManualResult.reason;
    fsStatusDot.className = 'status-dot';
    return;
  }

  const currentTurn = currentMode === 'online' ? onlineState?.sideToMove : localGame?.turn;
  if (!currentTurn) return;

  const sName = sideName(currentTurn);
  const isCheck = currentMode === 'online' ? onlineState?.check : localGame?.status().check;
  fsStatusText.textContent = isCheck ? `⚠ Chiếu — ${sName}` : `Lượt ${sName}`;
  fsStatusDot.className = `status-dot active ${currentTurn === 'w' ? 'white-turn' : 'black-turn'}`;
}

document.addEventListener('fullscreenchange', () => {
  if (!document.fullscreenElement && isFullscreen) {
    toggleFullscreen(false);
  }
});

// View Navigation
function showView(viewId) {
  const { isGameView } = activateView(viewId);

  if (!isGameView) {
    clearTimeout(eventToastTimer);
    eventToastEl.classList.add('hidden');
    updateCheckPresentation(false);
  }

  if (viewId === 'gamePicker' && homeRoomDirectoryController) {
    void homeRoomDirectoryController.refresh();
  }
}
function applyModeCapabilities() {
  renderModeCapabilities(GAME_CATALOG[currentGameType]);
}
function applySelectedGame(type) {
  const game = GAME_CATALOG[type];
  if (!game) return false;
  currentGameType = type;
  queryOne('#pageTitle').textContent = game.title;
  queryOne('#selectedGameKicker').textContent = game.kicker;
  queryOne('#selectedGameTitle').textContent = game.title + ' — Chọn chế độ';
  applyModeCapabilities();
  return true;
}

function selectGameType(type) {
  if (!applySelectedGame(type)) return;
  showView('modePicker');
}

function selectMode(mode) {
  const game = GAME_CATALOG[currentGameType];
  if (!game?.modes.includes(mode)) return;
  currentMode = mode;
  if (mode === 'online') initOnlineRoomLobby();
  else startLocalGame();
}

// ==========================================================
// LOCAL & BOT MODES
// ==========================================================
function startLocalGame() {
  disconnectWebSocket();
  cancelBotThinking();

  if (currentGameType === 'tycoon') {
    startTycoonGame();
    return;
  }

  localGame = loadLocalGame();
  selectedSquare = null;
  legalMoves = [];
  boardFlipped = false;
  onlineState = null;
  onlineUserSide = null;
  onlineUserSeat = null;
  onlineRole = 'lobby';

  configureGameUI();
  updatePieceGuide(null);
  renderLocal();
  maybeBot();
  showView('gameScreen');
}

function configureGameUI() {
  const isChess = currentGameType === 'chess';
  const isXiangqi = currentGameType === 'xiangqi';
  const isCaro = currentGameType === 'caro';
  const game = GAME_CATALOG[currentGameType];
  queryOne('#pageTitle').textContent = game?.title || 'IVS Board Games';
  const wrapClass = isChess ? 'chess-board-wrap' : isXiangqi ? 'xiangqi-board-wrap' : 'caro-board-wrap';
  const boardClass = isChess ? 'chess-board' : isXiangqi ? 'xiangqi-board' : 'caro-board';
  queryOne('#boardWrap').className = 'board-wrap ' + wrapClass;
  boardEl.className = 'board ' + boardClass;
  boardEl.setAttribute('aria-label', isChess ? 'Bàn cờ vua' : isXiangqi ? 'Bàn cờ tướng' : 'Bàn cờ ca-rô');
  queryOne('#copyFenBtn').classList.toggle('hidden', !isChess);
  queryOne('.captured-card').classList.toggle('hidden', isCaro);
  queryOne('#pieceGuideCard').classList.toggle('hidden', isCaro);

  if (currentMode === 'online') {
    modeBadgeEl.textContent = 'Trực tuyến LIVE';
    updateOnlineConnectionStatus(roomController.isOpen ? 'connected' : 'disconnected');
    botControlsCard.classList.add('hidden');
    undoBtn.classList.add('hidden');
    newGameBtn.classList.add('hidden');
    rematchBtn.classList.remove('hidden');
    leaveRoomBtn.classList.remove('hidden');
    liveDecisionActions.classList.remove('hidden');
  } else if (currentMode === 'bot') {
    modeBadgeEl.textContent = 'Đấu máy';
    onlineIndicatorEl.classList.add('hidden');
    botControlsCard.classList.remove('hidden');
    undoBtn.classList.remove('hidden');
    newGameBtn.classList.remove('hidden');
    rematchBtn.classList.add('hidden');
    leaveRoomBtn.classList.add('hidden');
    liveDecisionActions.classList.remove('hidden');
    drawBtn.textContent = '½ Chọn hòa';
    drawBtn.onclick = () => openGameDecision('draw');
    declineDrawBtn.classList.add('hidden');
    queryOne('#botLevel').value = localStorage.getItem('ivs-' + currentGameType + '-level') || '3';
    updateDifficultyNote();
  } else {
    modeBadgeEl.textContent = '2 người cùng máy';
    onlineIndicatorEl.classList.add('hidden');
    botControlsCard.classList.add('hidden');
    undoBtn.classList.remove('hidden');
    newGameBtn.classList.remove('hidden');
    rematchBtn.classList.add('hidden');
    leaveRoomBtn.classList.add('hidden');
    liveDecisionActions.classList.remove('hidden');
    drawBtn.textContent = '½ Chọn hòa';
    drawBtn.onclick = () => openGameDecision('draw');
    declineDrawBtn.classList.add('hidden');
  }
}

function renderLocal() {
  if (!localGame) return;

  if (currentGameType === 'caro') {
    const st = renderCaroBoard(boardEl, localGame, {
      flipped: boardFlipped,
      onClick: idx => handleSquareClick(idx)
    });

    let txt = 'Lượt ' + sideName(localGame.turn);
    let sub = currentMode === 'bot' ? (localGame.turn === 'w' ? 'Lượt của bạn' : 'Máy đang suy nghĩ...') : 'Đặt 5 quân liên tiếp để thắng';
    if (localManualResult) { txt = localManualResult.reason; sub = 'Ván cờ kết thúc'; }
    else if (st.winner) { txt = sideName(st.winner) + ' thắng — 5 quân liên tiếp'; sub = 'Ván cờ kết thúc'; }
    else if (st.draw) { txt = 'Hòa — Bàn cờ đã đầy'; sub = 'Ván cờ kết thúc'; }
    if (botThinking && !localManualResult) txt = 'Máy đang tính...';
    statusEl.textContent = txt;
    turnSubtextEl.textContent = sub;
    updateCheckPresentation(false);

    const topColor = boardFlipped ? 'w' : 'b';
    const bottomColor = boardFlipped ? 'b' : 'w';
    queryOne('#topPlayerName').textContent = currentMode === 'bot' ? (topColor === 'b' ? 'Máy' : 'Bạn') : 'Người chơi ' + sideName(topColor);
    queryOne('#bottomPlayerName').textContent = currentMode === 'bot' ? (bottomColor === 'w' ? 'Bạn' : 'Máy') : 'Người chơi ' + sideName(bottomColor);
    queryOne('#topPlayerBadge').textContent = sideName(topColor);
    queryOne('#bottomPlayerBadge').textContent = sideName(bottomColor);
    const activeGame = !localEngineFinished(st);
    queryOne('#topStatusDot').classList.toggle('active', activeGame && localGame.turn === topColor);
    queryOne('#bottomStatusDot').classList.toggle('active', activeGame && localGame.turn === bottomColor);
    updateLocalDecisionControls(st);
    renderHistory(localGame.history.map(h => h.notation));
    updateFullscreenHud();
    return;
  }

  if (currentGameType === 'xiangqi') {
    const st = localGame.status();
    const lastEntry = localGame.history.at(-1);
    const checkedKingChar = localGame.turn === 'w' ? 'K' : 'k';
    const inCheckIdx = st.check ? localGame.board.indexOf(checkedKingChar) : -1;
    renderXiangqiBoard(boardEl, localGame.board, {
      lastMove: lastEntry?.move ?? null,
      selectedIdx: selectedSquare,
      legalMovesArr: legalMoves,
      inCheckIdx,
      onClickFn: (idx) => handleSquareClick(idx),
      flipped: boardFlipped,
    });
    // Status
    let txt = `Lượt ${sideName(localGame.turn)}`;
    let sub = currentMode === 'bot' ? (localGame.turn === 'w' ? 'Lượt của bạn' : 'Máy đang suy nghĩ...') : 'Đang trong ván đấu';
    if (localManualResult) { txt = localManualResult.reason; sub = 'Ván cờ kết thúc'; }
    else if (st.checkmate) { txt = `Chiếu bí — ${sideName(st.winner)} thắng`; sub = 'Ván cờ kết thúc'; }
    else if (st.stalemate) { txt = 'Hòa — Hết nước hợp lệ'; sub = 'Ván cờ kết thúc'; }
    else if (st.repetition) { txt = 'Hòa — Lặp thế cờ 3 lần'; sub = 'Ván cờ kết thúc'; }
    else if (st.check) { txt += ' (Đang bị chiếu!)'; }
    if (botThinking && !localManualResult) txt = 'Máy đang tính...';
    statusEl.textContent = txt;
    turnSubtextEl.textContent = sub;
    updateCheckPresentation(!localManualResult && st.check);
    const topColor = boardFlipped ? 'w' : 'b';
    const bottomColor = boardFlipped ? 'b' : 'w';
    queryOne('#topPlayerName').textContent = currentMode === 'bot' ? (topColor === 'b' ? 'Máy' : 'Bạn') : sideName(topColor);
    queryOne('#bottomPlayerName').textContent = currentMode === 'bot' ? (bottomColor === 'w' ? 'Bạn' : 'Máy') : sideName(bottomColor);
    queryOne('#topPlayerBadge').textContent = sideName(topColor);
    queryOne('#bottomPlayerBadge').textContent = sideName(bottomColor);
    const activeGame = !localEngineFinished(st);
    queryOne('#topStatusDot').classList.toggle('active', activeGame && localGame.turn === topColor);
    queryOne('#bottomStatusDot').classList.toggle('active', activeGame && localGame.turn === bottomColor);
    updateLocalDecisionControls(st);
    renderHistory(localGame.history.map(h => h.notation));
    renderCapturedPieces(localGame.history.map(h => h.move));
    updateFullscreenHud();
    return;
  }

  const lastEntry = localGame.history.at(-1);
  const last = lastEntry?.move;
  const st = localGame.status();

  renderChessBoard(boardEl, localGame.board, {
    flipped: boardFlipped,
    lastMove: last,
    selectedIdx: selectedSquare,
    legalMoves,
    inCheck: st.check,
    sideToMove: localGame.turn,
    onClick: idx => handleSquareClick(idx)
  });
  // Status and indicators
  let txt = `Lượt ${sideName(localGame.turn)}`;
  let sub = currentMode === 'bot' ? (localGame.turn === 'w' ? 'Lượt của bạn' : 'Máy đang suy nghĩ...') : 'Đang trong ván đấu';

  if (localManualResult) {
    txt = localManualResult.reason;
    sub = 'Ván cờ kết thúc';
  } else if (st.checkmate) {
    txt = `Chiếu bí — ${sideName(st.winner)} thắng`;
    sub = 'Ván cờ kết thúc';
  } else if (st.stalemate) {
    txt = 'Hòa — Hết nước hợp lệ';
    sub = 'Ván cờ kết thúc';
  } else if (st.fifty) {
    txt = 'Hòa — Luật 50 nước';
    sub = 'Ván cờ kết thúc';
  } else if (st.repetition) {
    txt = 'Hòa — Lặp thế cờ 3 lần';
    sub = 'Ván cờ kết thúc';
  } else if (st.insufficient) {
    txt = 'Hòa — Không đủ lực lượng';
    sub = 'Ván cờ kết thúc';
  } else if (st.check) {
    txt += ' (Đang bị chiếu!)';
  }

  if (botThinking && !localManualResult) txt = 'Máy đang tính...';
  statusEl.textContent = txt;
  turnSubtextEl.textContent = sub;
  updateCheckPresentation(!localManualResult && st.check);

  const topColor = boardFlipped ? 'w' : 'b';
  const bottomColor = boardFlipped ? 'b' : 'w';

  queryOne('#topPlayerName').textContent = currentMode === 'bot' ? (topColor === 'b' ? 'Máy' : 'Bạn') : sideName(topColor);
  queryOne('#bottomPlayerName').textContent = currentMode === 'bot' ? (bottomColor === 'w' ? 'Bạn' : 'Máy') : sideName(bottomColor);
  queryOne('#topPlayerBadge').textContent = sideName(topColor);
  queryOne('#bottomPlayerBadge').textContent = sideName(bottomColor);

  const activeGame = !localEngineFinished(st);
  queryOne('#topStatusDot').classList.toggle('active', activeGame && localGame.turn === topColor);
  queryOne('#bottomStatusDot').classList.toggle('active', activeGame && localGame.turn === bottomColor);

  updateLocalDecisionControls(st);
  renderHistory(localGame.history.map(h => h.notation));
  renderCapturedPieces(localGame.history.map(h => h.move));
  updateFullscreenHud();
}

function renderHistory(notes) {
  renderMoveHistory(notes, { historyEl, moveCountEl });
}

function renderCapturedPieces(moves) {
  renderCapturedSidebar(moves, currentGameType);
}
async function handleSquareClick(idx) {
  if (currentMode === 'online') {
    handleOnlineSquareClick(idx);
    return;
  }

  const st = localGame.status();
  if (botThinking || localEngineFinished(st)) return;
  if (currentMode === 'bot' && localGame.turn === 'b') return;

  if (currentGameType === 'caro') {
    const moveResult = localGame.move(idx);
    if (!moveResult) return;
    selectedSquare = null;
    legalMoves = [];
    saveLocalGame();
    renderLocal();
    maybeBot();
    return;
  }

  const p = localGame.board[idx];

  if (selectedSquare !== null) {
    const moves = legalMoves.filter(m => m.to === idx);
    if (moves.length) {
      let moveResult = null;
      if (currentGameType === 'chess') {
        let promo = 'q';
        if (moves.some(m => m.promotion)) promo = await choosePromotion(localGame.turn);
        moveResult = localGame.move(selectedSquare, idx, promo);
      } else {
        moveResult = localGame.move(selectedSquare, idx);
      }
      selectedSquare = null;
      legalMoves = [];
      updatePieceGuide(null);
      saveLocalGame();
      showMoveNotice(moveResult, localGame.status());
      renderLocal();
      maybeBot();
      return;
    }
  }

  if (p && currentColor(p) === localGame.turn) {
    selectedSquare = idx;
    legalMoves = localGame.legalMoves(idx);
    updatePieceGuide(p);
    renderLocal();
  } else if (p) {
    // Tapped opponent piece: still show piece guide!
    selectedSquare = null;
    legalMoves = [];
    updatePieceGuide(p);
    renderLocal();
  } else {
    selectedSquare = null;
    legalMoves = [];
    updatePieceGuide(null);
    renderLocal();
  }
}

function choosePromotion(color) {
  return new Promise(resolve => {
    const choices = queryOne('#promotionChoices');
    choices.innerHTML = '';
    for (const type of ['q', 'r', 'b', 'n']) {
      const b = document.createElement('button');
      const p = color === 'w' ? type.toUpperCase() : type;
      b.innerHTML = CHESS_SVG[p];
      b.value = type;
      b.addEventListener('click', () => {
        promoDialog.close();
        resolve(type);
      }, { once: true });
      choices.appendChild(b);
    }
    promoDialog.showModal();
  });
}

function cancelBotThinking() {
  botRequestSequence++;
  botThinking = false;
}

function ensureBotWorker() {
  if (botWorker || typeof Worker === 'undefined') return botWorker;
  botWorker = new Worker(new URL('./bot-worker.js', import.meta.url), { type: 'module' });
  return botWorker;
}

function requestBotMove(game, level, requestId) {
  if (currentGameType === 'caro') {
    return Promise.resolve(chooseCaroBotMove(game, level));
  }

  const worker = ensureBotWorker();
  if (!worker) {
    const move = currentGameType === 'chess'
      ? chooseBotMove(game, level)
      : chooseXiangqiBotMove(game, level);
    return Promise.resolve(move);
  }

  const position = currentGameType === 'chess'
    ? { fen: game.toFEN() }
    : { board: game.board.slice(), turn: game.turn, fullmove: game.fullmove };

  return new Promise((resolve, reject) => {
    const onMessage = (event) => {
      if (event.data?.requestId !== requestId) return;
      cleanup();
      if (event.data.error) reject(new Error(event.data.error));
      else resolve(event.data.move || null);
    };
    const onError = (event) => {
      cleanup();
      reject(event.error || new Error(event.message || 'Bot Worker failed'));
    };
    const cleanup = () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    worker.postMessage({
      requestId,
      gameType: currentGameType,
      level,
      position
    });
  });
}

async function maybeBot() {
  if (!localGame || currentMode !== 'bot' || localGame.turn !== 'b' || localManualResult) return;
  const st = localGame.status();
  if (localEngineFinished(st)) return;

  const requestId = ++botRequestSequence;
  const level = Number(queryOne('#botLevel').value);
  botThinking = true;
  renderLocal();

  let moveResult = null;
  try {
    const move = await requestBotMove(localGame, level, requestId);
    if (requestId !== botRequestSequence || currentMode !== 'bot' || !localGame || localGame.turn !== 'b') return;

    if (move) {
      if (currentGameType === 'chess') {
        moveResult = localGame.move(move.from, move.to, move.promotion || 'q');
      } else {
        moveResult = localGame.move(move.from, move.to);
      }
    }
  } catch (error) {
    if (requestId === botRequestSequence) {
      console.error('Bot calculation failed', error);
      turnSubtextEl.textContent = 'Máy không thể tính nước đi. Hãy thử lại hoặc giảm độ khó.';
    }
  } finally {
    if (requestId !== botRequestSequence) return;
    botThinking = false;
    saveLocalGame();
    if (moveResult) showMoveNotice(moveResult, localGame.status());
    renderLocal();
  }
}

function updateDifficultyNote() {
  queryOne('#difficultyNote').textContent = DIFFICULTY[Number(queryOne('#botLevel').value)] || DIFFICULTY[3];
}

// ==========================================================
// TYCOON — VIETNAM 40-TILE LOCAL 2–4 PLAYER GAME
// ==========================================================

function loadTycoonGame() {
  return tycoonController.loadLocal(2, playerIdentity.profile);
}
function saveTycoonGame() {
  tycoonController.saveLocal(tycoonGame);
}
function startTycoonGame() {
  currentMode = 'local';
  localGame = null;
  tycoonGame = loadTycoonGame();
  selectedTycoonAsset = tycoonGame.propertiesOf(tycoonGame.currentPlayer)[0] ?? null;
  queryOne('#tycoonPlayerCount').value = String(tycoonGame.players.length);
  queryOne('#pageTitle').textContent = 'Cờ tỷ phú';
  modeBadgeEl.textContent = '2–4 người cùng máy';
  onlineIndicatorEl.classList.add('hidden');
  showView('tycoonScreen');
  renderTycoon();
}

function setTycoonAssetSelection(index) {
  if (!tycoonGame) return;
  const parsed = Number(index);
  if (!Number.isInteger(parsed) || tycoonGame.ownerOf(parsed)?.id !== tycoonGame.currentPlayer.id) return;
  selectedTycoonAsset = parsed;
  renderTycoon();
}

function renderTycoonBoard() {
  if (!tycoonGame) return;
  renderTycoonBoardView({
    game: tycoonGame,
    selectedAsset: selectedTycoonAsset,
    onSelectAsset: (index, ownedByCurrentPlayer) => {
      if (ownedByCurrentPlayer) {
        setTycoonAssetSelection(index);
      }
      showTycoonAssetInspector(tycoonGame, index);
    }
  });
}
function canControlCurrentTycoonPlayer() {
  return tycoonController.canControlCurrentPlayer({
    game: tycoonGame,
    isOnline: currentMode === 'online' && currentGameType === 'tycoon',
    state: onlineState,
    role: onlineRole,
    userSeat: onlineUserSeat,
    pendingRequestId: onlinePendingRequestId
  });
}
function renderTycoon() {
  if (!tycoonGame) return;

  const isOnlineTycoon = currentMode === 'online' && currentGameType === 'tycoon';
  const canAct = canControlCurrentTycoonPlayer();

  if (isOnlineTycoon) {
    updateOnlineConnectionStatus(roomController.isOpen ? 'connected' : 'disconnected');
  }

  selectedTycoonAsset = renderTycoonHud({
    game: tycoonGame,
    selectedAsset: selectedTycoonAsset,
    isOnline: isOnlineTycoon,
    canAct,
    roomCode: currentRoomCode,
    onlineState,
    onlineRole,
    onlineUserSeat,
    myPlayerId: isOnlineTycoon ? tycoonController.onlinePlayerId(onlineState, onlineUserSeat) : null,
    pendingRequestId: onlinePendingRequestId,
    statusEl,
    turnSubtextEl,
    modeBadgeEl
  });

  updateCheckPresentation(false);
  renderTycoonBoard();
  renderTycoonPlayersView(tycoonGame);
  selectedTycoonAsset = renderTycoonManagementView({
    game: tycoonGame,
    selectedAsset: selectedTycoonAsset,
    allowManage: canAct
  });
  tycoonAvatarCoordinator.renderLocal(tycoonGame, isOnlineTycoon);
  renderTycoonLogView(tycoonGame);
}
function initOnlineRoomLobby() {
  disconnectWebSocket();
  onlineState = null;
  onlineUserSide = null;
  onlineUserSeat = null;
  onlineRole = 'lobby';
  currentRoomCode = '';
  currentRoomPassword = '';
  currentHostToken = '';
  roomLobbyErrorEl.textContent = '';
  const params = new URLSearchParams(location.search);
  const invitedRoom = normalizeRoomCode(params.get('room'));
  if (invitedRoom) queryOne('#joinRoomCode').value = invitedRoom;
  roomDirectoryController.setCurrentGame(currentGameType);
  showView('roomLobby');
  void roomDirectoryController.refresh();
}

function prepareOnlineSidePicker() {
  const roomUrl = new URL(location.href);
  roomUrl.search = '';
  roomUrl.searchParams.set('game', currentGameType);
  roomUrl.searchParams.set('room', currentRoomCode);
  history.replaceState(null, '', roomUrl);

  if (currentGameType === 'tycoon') {
    queryOne('#tycoonActiveRoomCode').textContent = currentRoomCode || '—';
    queryOne('#tycoonSeatPickerDesc').textContent = 'Đang xác thực và tải trạng thái phòng...';
    showView('tycoonSeatPicker');
    return;
  }

  const isChess = currentGameType === 'chess';
  const isCaro = currentGameType === 'caro';
  queryOne('#sideWhiteSymbol').textContent = isChess ? '♔' : isCaro ? 'X' : '帥';
  queryOne('#sideBlackSymbol').textContent = isChess ? '♚' : isCaro ? 'O' : '將';
  queryOne('#sideWhiteName').textContent = isChess ? 'Trắng (Đi trước)' : isCaro ? 'X (Đi trước)' : 'Đỏ (Đi trước)';
  queryOne('#sideBlackName').textContent = isChess ? 'Đen (Đi sau)' : isCaro ? 'O (Đi sau)' : 'Đen (Đi sau)';
  queryOne('#activeRoomCode').textContent = currentRoomCode || '—';
  queryOne('#sidePickerDesc').textContent = 'Đang xác thực và tải trạng thái phòng...';
  showView('sidePicker');
}

function hostTokenKeyFor(gameType, roomCode) {
  return `ivs-room-host-${gameType}-${roomCode}`;
}

function hostTokenKey(roomCode) {
  return hostTokenKeyFor(currentGameType, roomCode);
}

function activateCreatedRoom({ gameType, result, password = '' }) {
  if (!applySelectedGame(gameType)) throw new Error('Loại trò chơi không hợp lệ.');
  currentMode = 'online';
  onlineState = null;
  onlineUserSide = null;
  onlineUserSeat = null;
  onlineRole = 'lobby';
  currentRoomCode = result.roomCode;
  currentRoomPassword = password;
  currentHostToken = result.hostToken;
  localStorage.setItem(hostTokenKeyFor(gameType, currentRoomCode), currentHostToken);
  void homeRoomDirectoryController?.refresh({ quiet: true });
  void roomDirectoryController.refresh({ quiet: true });
  prepareOnlineSidePicker();
  connectWebSocket();
}

function joinOnlineRoom(event) {
  event.preventDefault();
  roomLobbyErrorEl.textContent = '';
  const roomCode = normalizeRoomCode(queryOne('#joinRoomCode').value);
  if (!isValidRoomCode(roomCode)) {
    roomLobbyErrorEl.textContent = 'Vui lòng nhập mã phòng hợp lệ gồm 4–12 ký tự.';
    return;
  }
  currentRoomCode = roomCode;
  currentRoomPassword = queryOne('#joinRoomPassword').value;
  currentHostToken = localStorage.getItem(hostTokenKey(roomCode)) || '';
  prepareOnlineSidePicker();
  connectWebSocket();
}

function enterRoomFromDirectory(room) {
  if (!room || !applySelectedGame(room.gameType)) return;
  currentMode = 'online';
  const roomCode = normalizeRoomCode(room.roomCode);
  if (!isValidRoomCode(roomCode)) return;

  queryOne('#joinRoomCode').value = roomCode;
  queryOne('#joinRoomPassword').value = '';
  roomLobbyErrorEl.textContent = '';

  if (room.hasPassword) {
    // From the home directory the room form is initially hidden. Reuse the
    // normal online lobby so password entry, validation and errors remain in
    // one authoritative flow.
    initOnlineRoomLobby();
    queryOne('#joinRoomCode').value = roomCode;
    queryOne('#joinRoomPassword').value = '';
    roomLobbyErrorEl.textContent = `Phòng ${roomCode} có mật khẩu. Nhập mật khẩu rồi bấm "Vào phòng".`;
    queryOne('#joinRoomPassword').focus();
    queryOne('#joinRoomForm').scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  currentRoomCode = roomCode;
  currentRoomPassword = '';
  currentHostToken = localStorage.getItem(hostTokenKeyFor(room.gameType, roomCode)) || '';
  prepareOnlineSidePicker();
  connectWebSocket();
}

async function connectWebSocket() {
  try {
    await playerIdentity.ensureGuest();
    playerProfileController?.render();
  } catch {
    // LIVE remains available with the legacy device fallback if Identity is temporarily unavailable.
  }

  roomController.connect({
    url: CONFIG.getWsUrl(currentGameType, currentRoomCode),
    reconnecting: roomController.isReconnecting,
    helloMessage: {
      type: MSG_TYPES.HELLO,
      protocolVersion: PROTOCOL_VERSION,
      deviceId,
      playerToken: playerIdentity.token || null,
      password: currentRoomPassword,
      hostToken: currentHostToken || null
    }
  });
}

function disconnectWebSocket() {
  clearOnlinePendingMove();
  tycoonController.resetLobbyPending();
  roomController.disconnect();
}
function clearOnlinePendingMove() {
  onlinePendingRequestId = null;
  onlinePendingRevision = null;
  boardEl.classList.remove('pending');
}

function navigateAfterLeave(destination = 'lobby') {
  clearTimeout(leaveAckTimer);
  leaveAckTimer = null;
  pendingLeaveDestination = null;
  confirmLeaveRoomBtn.disabled = false;
  leaveRoomBtn.disabled = false;
  clearOnlinePendingMove();
  disconnectWebSocket();
  selectedSquare = null;
  legalMoves = [];
  onlineState = null;
  onlineUserSide = null;
  onlineUserSeat = null;
  onlineRole = 'lobby';
  history.replaceState(null, '', location.pathname);

  if (destination === 'home') {
    currentMode = 'bot';
    queryOne('#pageTitle').textContent = 'Chọn trò chơi';
    showView('gamePicker');
    return;
  }

  if (destination === 'mode') {
    showView('modePicker');
    return;
  }

  initOnlineRoomLobby();
}

function openGameDecision(type) {
  if (currentMode === 'online') {
    if (!onlineUserSide || onlineState?.status !== ROOM_STATUS.ACTIVE) return;
  } else if (!localGame || localEngineFinished()) {
    return;
  }

  pendingGameDecision = type;
  const copy = gameDecisionCopy({
    type,
    mode: currentMode,
    localTurn: localGame?.turn,
    gameType: currentGameType,
    sideName: sideNameForGame
  });

  gameDecisionIcon.textContent = copy.icon;
  gameDecisionTitle.textContent = copy.title;
  gameDecisionMessage.textContent = copy.message;
  confirmGameDecisionBtn.textContent = copy.confirm;
  confirmGameDecisionBtn.classList.toggle('danger', copy.danger);
  confirmGameDecisionBtn.classList.toggle('draw-btn', !copy.danger);
  gameDecisionDialog.showModal();
}
function confirmGameDecision() {
  const type = pendingGameDecision;
  pendingGameDecision = null;
  gameDecisionDialog.close();
  if (!type) return;

  if (currentMode === 'online') {
    if (type === 'resign') {
      resignBtn.disabled = true;
      sendWsMessage({
        type: MSG_TYPES.RESIGN,
        protocolVersion: PROTOCOL_VERSION,
        deviceId
      });
      return;
    }

    if (type === 'draw') {
      drawBtn.disabled = true;
      sendWsMessage({
        type: MSG_TYPES.OFFER_DRAW,
        protocolVersion: PROTOCOL_VERSION,
        deviceId
      });
    }
    return;
  }

  if (!localGame || localEngineFinished()) return;

  cancelBotThinking();
  localManualResult = type === 'resign'
    ? createLocalResignResult({
        mode: currentMode,
        turn: localGame.turn,
        gameType: currentGameType
      })
    : createLocalDrawResult({ mode: currentMode });

  selectedSquare = null;
  legalMoves = [];
  updatePieceGuide(null);
  saveLocalGame();
  renderLocal();
}

function respondToDraw(accept) {
  if (!onlineState?.drawOffer || !onlineUserSide) return;
  sendWsMessage({
    type: MSG_TYPES.RESPOND_DRAW,
    protocolVersion: PROTOCOL_VERSION,
    deviceId,
    accept
  });
}

function cancelDrawOffer() {
  sendWsMessage({
    type: MSG_TYPES.CANCEL_DRAW,
    protocolVersion: PROTOCOL_VERSION,
    deviceId
  });
}

function requestLeaveRoom(destination = 'lobby') {
  if (currentMode !== 'online' || !currentRoomCode) {
    navigateAfterLeave(destination);
    return;
  }

  pendingLeaveDestination = destination;
  leaveRoomMessageEl.textContent = leaveRoomCopy({
    gameType: currentGameType,
    onlineState,
    onlineUserSeat,
    onlineUserSide
  });

  if (!leaveRoomDialog.open) leaveRoomDialog.showModal();
}
function confirmLeaveRoom() {
  const destination = pendingLeaveDestination || 'lobby';
  leaveRoomDialog.close();

  if (!roomController.isOpen) {
    navigateAfterLeave(destination);
    return;
  }

  confirmLeaveRoomBtn.disabled = true;
  leaveRoomBtn.disabled = true;
  onlineStatusTextEl.textContent = 'Đang rời phòng...';
  sendWsMessage({
    type: MSG_TYPES.LEAVE,
    protocolVersion: PROTOCOL_VERSION,
    deviceId
  });
  roomController.suppressReconnect();

  // Network fallback: explicit server acknowledgement is preferred, but UX
  // must never trap the user if the socket disappears at the same moment.
  clearTimeout(leaveAckTimer);
  leaveAckTimer = setTimeout(() => navigateAfterLeave(destination), 1500);
}

function updateOnlineConnectionStatus(statusState) {
  renderConnectionStatus(currentMode, statusState, {
    indicatorEl: onlineIndicatorEl,
    statusTextEl: onlineStatusTextEl
  });
}

queryOne('#reconnectBtn')?.addEventListener('click', () => {
  if (currentMode === 'online' && currentRoomCode) roomController.reconnectNow();
});

window.addEventListener('online', () => {
  if (currentMode === 'online' && currentRoomCode && !roomController.isOpen) {
    roomController.reconnectNow();
  }
});

window.addEventListener('offline', () => {
  if (currentMode === 'online') roomController.markOffline();
});
function sendWsMessage(msg) {
  return roomController.send(msg);
}

function showConnectionError(txt) {
  if (turnSubtextEl) turnSubtextEl.textContent = txt;
}

function handleServerMessage(msg) {
  if (msg.type === MSG_TYPES.STATE) {
    if (msg.gameType === 'tycoon' || currentGameType === 'tycoon') {
      const previousRevision = onlineState?.revision ?? null;
      onlineState = msg;
      onlineUserSide = null;
      onlineUserSeat = Number.isInteger(msg.userSeat) ? msg.userSeat : null;
      onlineRole = msg.role || 'lobby';

      tycoonController.reconcileAuthoritativeState(msg, onlineUserSeat);

      if (onlinePendingRequestId && previousRevision !== null && msg.revision > previousRevision) {
        clearOnlinePendingMove();
      }

      updateTycoonSeatPickerUI();

      if (msg.game) {
        const restored = tycoonController.restoreSnapshot(msg.game);
        if (restored) {
          tycoonGame = restored;
          renderTycoon();
        }
      }

      const mayEnterGame = msg.status === ROOM_STATUS.ACTIVE || msg.status === ROOM_STATUS.FINISHED;
      if (mayEnterGame && (Number.isInteger(onlineUserSeat) || onlineRole === 'spectator')) {
        showView('tycoonScreen');
        renderTycoon();
      } else if (msg.status === ROOM_STATUS.WAITING && !queryOne('#tycoonSeatPicker').classList.contains('hidden')) {
        updateTycoonSeatPickerUI();
      } else if (msg.status === ROOM_STATUS.WAITING && !queryOne('#tycoonScreen').classList.contains('hidden')) {
        showView('tycoonSeatPicker');
      }
      return;
    }

    const previousMoveCount = onlineState?.room?.code === msg.room?.code ? onlineState.moveHistory.length : null;
    onlineState = msg;
    onlineUserSide = msg.userSide;
    onlineRole = msg.role || 'lobby';
    if (onlinePendingRequestId && onlinePendingRevision !== null && msg.revision > onlinePendingRevision) {
      clearOnlinePendingMove();
    }
    if (previousMoveCount !== null && msg.moveHistory.length > previousMoveCount) {
      showMoveNotice(msg.lastMove, { check: msg.check, turn: msg.sideToMove });
    }

    // Update Side Picker view if open
    updateSidePickerUI();

    // If currently on Game Screen, render board
    if (!queryOne('#gameScreen').classList.contains('hidden')) {
      renderOnline();
    }
    if (onlineRole === 'spectator' && !queryOne('#sidePicker').classList.contains('hidden')) {
      boardFlipped = false;
      configureGameUI();
      renderOnline();
      showView('gameScreen');
    }
  } else if (msg.type === MSG_TYPES.AUTH_ACCEPTED) {
    if (currentGameType === 'tycoon') {
      queryOne('#tycoonSeatPickerDesc').textContent = `Phòng ${msg.roomCode} · Chọn ghế hoặc vào xem.`;
      queryOne('#tycoonActiveRoomCode').textContent = msg.roomCode;
      queryOne('#tycoonHostSpectatorControl').classList.toggle('hidden', !msg.isHost);
      queryOne('#tycoonAllowSpectatorsToggle').checked = msg.allowSpectators;
      queryOne('#tycoonSpectatorBtn').disabled = !msg.allowSpectators;
      queryOne('#tycoonSpectatorStatus').textContent = msg.allowSpectators ? '' : 'Chủ phòng đang tắt quyền khán giả';
      queryOne('#tycoonStartOnlineBtn').classList.toggle('hidden', !msg.isHost);
    } else {
      queryOne('#sidePickerDesc').textContent = `Phòng ${msg.roomCode} · Chọn bên chơi hoặc vào xem.`;
      queryOne('#activeRoomCode').textContent = msg.roomCode;
      queryOne('#hostSpectatorControl').classList.toggle('hidden', !msg.isHost);
      queryOne('#allowSpectatorsToggle').checked = msg.allowSpectators;
      queryOne('#spectatorBtn').disabled = !msg.allowSpectators;
      queryOne('#spectatorStatus').textContent = msg.allowSpectators ? '' : 'Chủ phòng đang tắt quyền khán giả';
    }
  } else if (msg.type === MSG_TYPES.RESIGN_ACCEPTED) {
    resignBtn.disabled = false;
    queryOne('#tycoonOnlineResignBtn')?.removeAttribute('disabled');
  } else if (msg.type === MSG_TYPES.DRAW_OFFERED) {
    drawBtn.disabled = false;
  } else if (msg.type === MSG_TYPES.DRAW_RESPONSE) {
    drawBtn.disabled = false;
  } else if (msg.type === MSG_TYPES.LEAVE_ACCEPTED) {
    const destination = pendingLeaveDestination || 'lobby';
    confirmLeaveRoomBtn.disabled = false;
    leaveRoomBtn.disabled = false;
    navigateAfterLeave(destination);
  } else if (msg.type === MSG_TYPES.AUTH_REJECTED) {
    alert(msg.reason || 'Không thể vào phòng');
    disconnectWebSocket();
    showView('roomLobby');
  } else if (msg.type === MSG_TYPES.CLAIM_SEAT_ACCEPTED) {
    tycoonController.settleSeatClaim();
    onlineUserSeat = Number(msg.seat);
    onlineRole = 'player';
    updateTycoonSeatPickerUI();
  } else if (msg.type === MSG_TYPES.CLAIM_SEAT_REJECTED) {
    tycoonController.settleSeatClaim();
    updateTycoonSeatPickerUI();
    queryOne('#tycoonSeatPickerDesc').textContent = msg.reason || 'Không thể chọn ghế này';
  } else if (tycoonAvatarCoordinator.handleServerMessage(msg)) {
  } else if (msg.type === MSG_TYPES.START_GAME_ACCEPTED) {
    tycoonController.settleStart();
    queryOne('#tycoonStartOnlineBtn').disabled = true;
    queryOne('#tycoonStartOnlineBtn').textContent = 'Đang tải ván...';
    queryOne('#tycoonSeatPickerDesc').textContent = 'Đã bắt đầu ván · Đang tải trạng thái...';
  } else if (msg.type === MSG_TYPES.TYCOON_ACTION_ACCEPTED) {
    // Keep controls locked until the authoritative STATE with a newer revision arrives.
  } else if (msg.type === MSG_TYPES.TYCOON_ACTION_REJECTED) {
    if (!msg.requestId || msg.requestId === onlinePendingRequestId) clearOnlinePendingMove();
    const message = msg.reason || 'Hành động không được máy chủ chấp nhận';
    if (turnSubtextEl) turnSubtextEl.textContent = message;
  } else if (msg.type === MSG_TYPES.CLAIM_ACCEPTED) {
    onlineUserSide = msg.side;
    boardFlipped = msg.side === 'b';
    configureGameUI();
    renderOnline();
    showView('gameScreen');
  } else if (msg.type === MSG_TYPES.CLAIM_REJECTED) {
    alert(msg.reason || 'Không thể chọn bên này');
  } else if (msg.type === MSG_TYPES.REMATCH_REQUESTED) {
    if (currentGameType === 'tycoon') {
      const btn = queryOne('#tycoonOnlineRematchBtn');
      if (btn && Number.isInteger(onlineUserSeat) && msg.bySeat !== onlineUserSeat) {
        btn.textContent = 'Người chơi khác muốn chơi lại — Chấp nhận';
        btn.classList.remove('hidden');
      }
    } else if (onlineUserSide && msg.bySide !== onlineUserSide) {
      rematchBtn.textContent = 'Đối thủ muốn chơi lại — Chấp nhận';
      rematchBtn.classList.remove('hidden');
    }
  } else if (msg.type === MSG_TYPES.MOVE_REJECTED) {
    if (!msg.requestId || msg.requestId === onlinePendingRequestId) clearOnlinePendingMove();
    showConnectionError(msg.reason || 'Nước đi không được máy chủ chấp nhận');
    if (onlineState) renderOnline();
  } else if (msg.type === MSG_TYPES.ERROR) {
    const message = msg.message || 'Yêu cầu không hợp lệ';
    showConnectionError(message);
    if (currentGameType === 'tycoon' && !queryOne('#tycoonSeatPicker').classList.contains('hidden')) {
      tycoonController.resetLobbyPending();
      updateTycoonSeatPickerUI();
      queryOne('#tycoonSeatPickerDesc').textContent = message;
    }
  }
}

function updateSidePickerUI() {
  if (!onlineState) return;
  const w = onlineState.players.w;
  const b = onlineState.players.b;

  const wBtn = queryOne('#sideWhiteBtn');
  const bBtn = queryOne('#sideBlackBtn');
  queryOne('#activeRoomCode').textContent = onlineState.room?.code || currentRoomCode;
  const hostCanChangeGame = Boolean(onlineState.room?.isHost && onlineState.status === ROOM_STATUS.WAITING);
  queryOne('#hostSpectatorControl').classList.toggle('hidden', !onlineState.room?.isHost);
  queryOne('#hostChangeGameBtn').classList.toggle('hidden', !hostCanChangeGame);
  queryOne('#allowSpectatorsToggle').checked = Boolean(onlineState.room?.allowSpectators);
  queryOne('#spectatorBtn').disabled = !onlineState.room?.allowSpectators;
  queryOne('#spectatorStatus').textContent = onlineState.room?.allowSpectators ? `${onlineState.spectatorsCount || 0} khán giả đang xem` : 'Chủ phòng đang tắt quyền khán giả';

  if (w.occupied) {
    queryOne('#sideWhiteStatus').textContent = (onlineState.userSide === 'w') ? '● Bạn đang giữ bên này' : '● Đã có người chơi';
    wBtn.style.opacity = (onlineState.userSide === 'w') ? '1' : '0.6';
    wBtn.disabled = onlineState.userSide !== 'w';
  } else {
    queryOne('#sideWhiteStatus').textContent = 'Sẵn sàng chọn';
    wBtn.style.opacity = '1';
    wBtn.disabled = false;
  }

  if (b.occupied) {
    queryOne('#sideBlackStatus').textContent = (onlineState.userSide === 'b') ? '● Bạn đang giữ bên này' : '● Đã có người chơi';
    bBtn.style.opacity = (onlineState.userSide === 'b') ? '1' : '0.6';
    bBtn.disabled = onlineState.userSide !== 'b';
  } else {
    queryOne('#sideBlackStatus').textContent = 'Sẵn sàng chọn';
    bBtn.style.opacity = '1';
    bBtn.disabled = false;
  }
}

function updateTycoonSeatPickerUI() {
  renderTycoonLobby({
    state: onlineState,
    roomCode: currentRoomCode,
    userSeat: onlineUserSeat,
    pendingSeatClaim: tycoonController.pendingSeatClaim,
    pendingAvatarId: tycoonController.pendingAvatarId,
    startPending: tycoonController.startPending
  });
}
function claimTycoonSeat(seat) {
  if (!tycoonController.beginSeatClaim(seat)) return;
  updateTycoonSeatPickerUI();

  const sent = sendWsMessage({
    type: MSG_TYPES.CLAIM_SEAT,
    protocolVersion: PROTOCOL_VERSION,
    seat,
    deviceId
  });

  if (!sent) {
    tycoonController.settleSeatClaim();
    updateTycoonSeatPickerUI();
    queryOne('#tycoonSeatPickerDesc').textContent = 'Mất kết nối tới phòng. Hãy bấm Thử lại rồi chọn ghế.';
  }
}
function startTycoonOnlineGame() {
  if (!tycoonController.beginStart()) return;
  updateTycoonSeatPickerUI();

  const sent = sendWsMessage({
    type: MSG_TYPES.START_GAME,
    protocolVersion: PROTOCOL_VERSION,
    deviceId
  });

  if (!sent) {
    tycoonController.settleStart();
    updateTycoonSeatPickerUI();
    queryOne('#tycoonSeatPickerDesc').textContent = 'Mất kết nối tới phòng. Hãy bấm Thử lại trước khi bắt đầu.';
  }
}
function joinTycoonAsSpectator() {
  sendWsMessage({ type: MSG_TYPES.JOIN_SPECTATOR, protocolVersion: PROTOCOL_VERSION });
}

function sendTycoonAction(action, propertyIndex = null) {
  if (
    currentMode !== 'online' ||
    currentGameType !== 'tycoon' ||
    !onlineState ||
    onlineState.status !== ROOM_STATUS.ACTIVE ||
    onlinePendingRequestId
  ) return false;

  const request = tycoonController.createOnlineAction({
    state: onlineState,
    action,
    propertyIndex,
    deviceId
  });
  if (!request) return false;

  onlinePendingRequestId = request.requestId;
  onlinePendingRevision = onlineState.revision;
  const sent = sendWsMessage(request.message);
  if (!sent) {
    clearOnlinePendingMove();
    return false;
  }

  renderTycoon();
  return true;
}

function performTycoonAction(action, propertyIndex = null) {
  if (!tycoonGame) return false;
  if (currentMode === 'online') return sendTycoonAction(action, propertyIndex);

  const applied = tycoonController.applyLocalAction(tycoonGame, action, propertyIndex);
  if (!applied) return false;
  saveTycoonGame();
  renderTycoon();
  return true;
}
function claimSide(side) {
  sendWsMessage({
    type: MSG_TYPES.CLAIM_SIDE,
    protocolVersion: PROTOCOL_VERSION,
    side,
    deviceId
  });
}

function joinAsSpectator() {
  sendWsMessage({ type: MSG_TYPES.JOIN_SPECTATOR, protocolVersion: PROTOCOL_VERSION });
}

function renderOnline() {
  if (!onlineState) return;
  onlineStatusTextEl.textContent = currentRoomCode ? `LIVE · ${currentRoomCode}` : 'Trực tuyến';

  if (currentGameType === 'caro') {
    renderCaroPosition(boardEl, onlineState.board, {
      flipped: boardFlipped,
      lastMove: onlineState.lastMove ?? null,
      winningLine: onlineState.winningLine || [],
      onClick: idx => handleOnlineSquareClick(idx)
    });
  } else if (currentGameType === 'xiangqi') {
    const checkedKingChar = onlineState.sideToMove === 'w' ? 'K' : 'k';
    const inCheckIdx = onlineState.check ? onlineState.board.indexOf(checkedKingChar) : -1;
    renderXiangqiBoard(boardEl, onlineState.board, {
      lastMove: onlineState.lastMove ?? null,
      selectedIdx: selectedSquare,
      legalMovesArr: legalMoves,
      inCheckIdx,
      onClickFn: (idx) => handleOnlineSquareClick(idx),
      flipped: boardFlipped,
    });
  } else {
    renderChessBoard(boardEl, onlineState.board, {
      flipped: boardFlipped,
      lastMove: onlineState.lastMove,
      selectedIdx: selectedSquare,
      legalMoves,
      inCheck: onlineState.check,
      sideToMove: onlineState.sideToMove,
      onClick: idx => handleOnlineSquareClick(idx)
    });
  }

  const { text: txt, subtext: sub } = deriveOnlineGameStatus({
    state: onlineState,
    userSide: onlineUserSide,
    pendingRequestId: onlinePendingRequestId,
    sideName
  });

  boardEl.classList.toggle('pending', Boolean(onlinePendingRequestId));
  statusEl.textContent = txt;
  turnSubtextEl.textContent = sub;
  updateCheckPresentation(onlineState.check && onlineState.status === ROOM_STATUS.ACTIVE);

  // Player bars
  const topColor = boardFlipped ? 'w' : 'b';
  const bottomColor = boardFlipped ? 'b' : 'w';

  const topOwner = onlineState.players[topColor];
  const bottomOwner = onlineState.players[bottomColor];

  const onlinePlayerLabel = (owner, color) => {
    if (!owner?.occupied) return 'Đang chờ...';
    const profileName = owner.displayName || (onlineUserSide === color ? playerIdentity.profile?.displayName : '') || 'Đối thủ';
    const avatar = avatarFor(owner.avatarId || (onlineUserSide === color ? playerIdentity.profile?.avatarId : null));
    return `${avatar.symbol} ${profileName}${onlineUserSide === color ? ' · Bạn' : ''}`;
  };

  queryOne('#topPlayerName').textContent = onlinePlayerLabel(topOwner, topColor);
  queryOne('#bottomPlayerName').textContent = onlinePlayerLabel(bottomOwner, bottomColor);
  queryOne('#topPlayerBadge').textContent = sideName(topColor);
  queryOne('#bottomPlayerBadge').textContent = sideName(bottomColor);

  queryOne('#topStatusDot').classList.toggle('active', topOwner.connected && onlineState.sideToMove === topColor && onlineState.status === ROOM_STATUS.ACTIVE);
  queryOne('#bottomStatusDot').classList.toggle('active', bottomOwner.connected && onlineState.sideToMove === bottomColor && onlineState.status === ROOM_STATUS.ACTIVE);

  // Live decision controls
  const isActivePlayer = Boolean(
    onlineUserSide &&
    onlineRole === 'player' &&
    onlineState.status === ROOM_STATUS.ACTIVE
  );
  liveDecisionActions.classList.toggle('hidden', !isActivePlayer);
  leaveRoomBtn.classList.toggle('hidden', currentMode !== 'online');

  resignBtn.disabled = !isActivePlayer;
  drawBtn.disabled = !isActivePlayer;
  declineDrawBtn.classList.add('hidden');

  if (isActivePlayer) {
    const drawOffer = onlineState.drawOffer;
    if (!drawOffer) {
      drawBtn.textContent = '½ Đề nghị hòa';
      drawBtn.onclick = () => openGameDecision('draw');
    } else if (drawOffer.bySide === onlineUserSide) {
      drawBtn.textContent = 'Hủy đề nghị hòa';
      drawBtn.disabled = false;
      drawBtn.onclick = cancelDrawOffer;
    } else {
      drawBtn.textContent = 'Chấp nhận hòa';
      drawBtn.disabled = false;
      drawBtn.onclick = () => respondToDraw(true);
      declineDrawBtn.classList.remove('hidden');
    }
  }

  // Rematch button state
  if (onlineState.status === ROOM_STATUS.FINISHED) {
    rematchBtn.classList.remove('hidden');
    if (onlineUserSide && onlineState.rematchState[onlineUserSide]) {
      rematchBtn.textContent = 'Đã gửi yêu cầu chơi lại...';
    } else if (onlineUserSide && onlineState.rematchState[onlineUserSide === 'w' ? 'b' : 'w']) {
      rematchBtn.textContent = 'Đối thủ muốn chơi lại — Chấp nhận';
    } else {
      rematchBtn.textContent = 'Yêu cầu chơi lại';
    }
  } else {
    rematchBtn.classList.add('hidden');
  }

  renderHistory(onlineState.moveHistory.map(h => h.notation));
  renderCapturedPieces(onlineState.moveHistory);
  updateFullscreenHud();
}

async function handleOnlineSquareClick(idx) {
  if (!onlineState || onlineState.status !== ROOM_STATUS.ACTIVE) return;
  if (onlinePendingRequestId) return;
  if (!onlineUserSide || onlineState.sideToMove !== onlineUserSide) return;

  const p = onlineState.board[idx];

  if (currentGameType === 'caro') {
    if (p) return;
    const requestId = createRequestId('caro');
    onlinePendingRequestId = requestId;
    onlinePendingRevision = onlineState.revision;

    const sent = sendWsMessage({
      type: MSG_TYPES.MOVE,
      protocolVersion: PROTOCOL_VERSION,
      requestId,
      expectedRevision: onlineState.revision,
      from: idx,
      to: idx,
      promotion: null,
      deviceId
    });

    if (!sent) {
      clearOnlinePendingMove();
      showConnectionError('Mất kết nối tới phòng. Hãy thử lại.');
      return;
    }

    renderOnline();
    return;
  }

  // Helper engine to calculate legal moves locally
  let engine;
  if (currentGameType === 'chess') {
    engine = new ChessGame(onlineState.fen);
  } else {
    engine = new XiangqiGame();
    engine.board = onlineState.board.slice();
    engine.turn = onlineState.sideToMove;
  }

  if (selectedSquare !== null) {
    const moves = legalMoves.filter(m => m.to === idx);
    if (moves.length) {
      let promo = null;
      if (currentGameType === 'chess' && moves.some(m => m.promotion)) {
        promo = await choosePromotion(onlineUserSide);
      }

      // Send Authoritative MOVE request
      const reqId = createRequestId('move');
      onlinePendingRequestId = reqId;
      onlinePendingRevision = onlineState.revision;
      sendWsMessage({
        type: MSG_TYPES.MOVE,
        protocolVersion: PROTOCOL_VERSION,
        requestId: reqId,
        expectedRevision: onlineState.revision,
        from: selectedSquare,
        to: idx,
        promotion: promo,
        deviceId
      });

      selectedSquare = null;
      legalMoves = [];
      updatePieceGuide(null);
      renderOnline();
      return;
    }
  }

  if (p && currentColor(p) === onlineUserSide) {
    selectedSquare = idx;
    legalMoves = engine.legalMoves(idx);
    updatePieceGuide(p);
    renderOnline();
  } else if (p) {
    selectedSquare = null;
    legalMoves = [];
    updatePieceGuide(p);
    renderOnline();
  } else {
    selectedSquare = null;
    legalMoves = [];
    updatePieceGuide(null);
    renderOnline();
  }
}

// ==========================================================
// EVENT LISTENERS & INITIALIZATION
// ==========================================================
// Game Picker
queryAll('[data-game]').forEach(b => b.addEventListener('click', () => selectGameType(b.dataset.game)));

// Mode Picker
queryAll('[data-mode]').forEach(b => b.addEventListener('click', () => selectMode(b.dataset.mode)));

// Online Room Lobby
queryOne('#joinRoomForm').addEventListener('submit', joinOnlineRoom);

// Side Picker
queryOne('#sideWhiteBtn').addEventListener('click', () => claimSide('w'));
queryOne('#sideBlackBtn').addEventListener('click', () => claimSide('b'));
queryOne('#spectatorBtn').addEventListener('click', joinAsSpectator);
queryOne('#hostChangeGameBtn').addEventListener('click', () => roomActionsController?.openChangeGame());
queryOne('#tycoonHostChangeGameBtn').addEventListener('click', () => roomActionsController?.openChangeGame());

queryOne('#allowSpectatorsToggle').addEventListener('change', event => {
  sendWsMessage({
    type: MSG_TYPES.SET_SPECTATORS,
    protocolVersion: PROTOCOL_VERSION,
    allowSpectators: event.target.checked
  });
});
queryOne('#copyRoomLinkBtn').addEventListener('click', async () => {
  const inviteUrl = new URL(location.href);
  inviteUrl.search = '';
  inviteUrl.searchParams.set('game', currentGameType);
  inviteUrl.searchParams.set('room', currentRoomCode);
  try {
    await navigator.clipboard.writeText(inviteUrl.toString());
    queryOne('#copyRoomLinkBtn').textContent = 'Đã sao chép!';
    setTimeout(() => { queryOne('#copyRoomLinkBtn').textContent = 'Sao chép link mời'; }, 1200);
  } catch {
    roomLobbyErrorEl.textContent = 'Không thể sao chép tự động. Hãy sao chép URL trên trình duyệt.';
  }
});

// Tycoon Seat Picker
queryAll('[data-tycoon-seat]').forEach(button => {
  button.addEventListener('click', () => claimTycoonSeat(Number(button.dataset.tycoonSeat)));
});
queryOne('#tycoonStartOnlineBtn').addEventListener('click', startTycoonOnlineGame);
queryOne('#tycoonSpectatorBtn').addEventListener('click', joinTycoonAsSpectator);
queryOne('#tycoonAllowSpectatorsToggle').addEventListener('change', event => {
  sendWsMessage({
    type: MSG_TYPES.SET_SPECTATORS,
    protocolVersion: PROTOCOL_VERSION,
    allowSpectators: event.target.checked
  });
});
queryOne('#tycoonCopyRoomLinkBtn').addEventListener('click', async () => {
  const inviteUrl = new URL(location.href);
  inviteUrl.search = '';
  inviteUrl.searchParams.set('game', 'tycoon');
  inviteUrl.searchParams.set('room', currentRoomCode);
  try {
    await navigator.clipboard.writeText(inviteUrl.toString());
    queryOne('#tycoonCopyRoomLinkBtn').textContent = 'Đã sao chép!';
    setTimeout(() => { queryOne('#tycoonCopyRoomLinkBtn').textContent = 'Sao chép link mời'; }, 1200);
  } catch {
    queryOne('#tycoonSeatPickerDesc').textContent = 'Không thể sao chép tự động. Hãy sao chép URL trên trình duyệt.';
  }
});

// Topbar Navigation
queryOne('#homeBtn').addEventListener('click', () => {
  if (currentMode === 'online' && currentRoomCode) {
    requestLeaveRoom('home');
    return;
  }
  disconnectWebSocket();
  cancelBotThinking();
  history.replaceState(null, '', location.pathname);
  queryOne('#pageTitle').textContent = 'Chọn trò chơi';
  showView('gamePicker');
});

// Fullscreen Actions
queryOne('#fullscreenBtn').addEventListener('click', () => toggleFullscreen());
queryOne('#enterFsBtn').addEventListener('click', () => toggleFullscreen(true));
queryOne('#fsExitBtn').addEventListener('click', () => toggleFullscreen(false));
queryOne('#fsFlipBtn').addEventListener('click', () => {
  boardFlipped = !boardFlipped;
  if (currentGameType === 'tycoon') renderTycoon();
  else if (currentMode === 'online') renderOnline();
  else renderLocal();
});

// Actions
queryOne('#newGameBtn').addEventListener('click', () => {
  if (currentMode === 'online') return;
  cancelBotThinking();
  localManualResult = null;
  localGame = createLocalEngine();
  selectedSquare = null;
  legalMoves = [];
  updatePieceGuide(null);
  saveLocalGame();
  renderLocal();
  maybeBot();
});

queryOne('#undoBtn').addEventListener('click', () => {
  if (currentMode === 'online' || botThinking || !localGame || localManualResult) return;
  if (currentMode === 'bot') {
    localGame.undo();
    if (localGame.turn === 'b') localGame.undo();
  } else {
    localGame.undo();
  }
  selectedSquare = null;
  legalMoves = [];
  updatePieceGuide(null);
  saveLocalGame();
  renderLocal();
});

queryOne('#flipBtn').addEventListener('click', () => {
  boardFlipped = !boardFlipped;
  if (currentMode === 'online') renderOnline();
  else renderLocal();
});

queryOne('#rematchBtn').addEventListener('click', () => {
  if (currentMode === 'online') {
    sendWsMessage({
      type: MSG_TYPES.REQUEST_REMATCH,
      protocolVersion: PROTOCOL_VERSION,
      deviceId
    });
  }
});

queryOne('#changeModeBtn').addEventListener('click', () => {
  if (currentMode === 'online' && currentRoomCode) {
    requestLeaveRoom('mode');
    return;
  }
  disconnectWebSocket();
  cancelBotThinking();
  history.replaceState(null, '', location.pathname);
  showView('modePicker');
});

resignBtn.addEventListener('click', () => openGameDecision('resign'));
declineDrawBtn.addEventListener('click', () => respondToDraw(false));
confirmGameDecisionBtn.addEventListener('click', confirmGameDecision);
cancelGameDecisionBtn.addEventListener('click', () => {
  pendingGameDecision = null;
  gameDecisionDialog.close();
});

leaveRoomBtn.addEventListener('click', () => requestLeaveRoom('lobby'));
confirmLeaveRoomBtn.addEventListener('click', confirmLeaveRoom);
cancelLeaveRoomBtn.addEventListener('click', () => {
  pendingLeaveDestination = null;
  leaveRoomDialog.close();
});

queryOne('#botLevel').addEventListener('change', () => {
  if (!currentGameType) return;
  localStorage.setItem(`ivs-${currentGameType}-level`, queryOne('#botLevel').value);
  updateDifficultyNote();
  maybeBot();
});

for (const [selector, action] of [
  ['#tycoonRollBtn', 'ROLL_DICE'],
  ['#tycoonBuyBtn', 'BUY_PROPERTY'],
  ['#tycoonSkipBtn', 'SKIP_PURCHASE'],
  ['#tycoonEndTurnBtn', 'END_TURN'],
  ['#tycoonPayDebtBtn', 'PAY_DEBT'],
  ['#tycoonPayJailBtn', 'PAY_JAIL'],
  ['#tycoonUseJailCardBtn', 'USE_JAIL_CARD']
]) {
  queryOne(selector).addEventListener('click', () => performTycoonAction(action));
}

queryOne('#tycoonBankruptBtn').addEventListener('click', () => {
  if (!tycoonGame) return;
  if (!confirm('Tuyên bố phá sản? Bạn sẽ bị loại khỏi ván chơi.')) return;
  performTycoonAction('DECLARE_BANKRUPTCY');
});

queryOne('#tycoonAssetSelect').addEventListener('change', event => {
  const index = Number(event.target.value);
  if (Number.isInteger(index)) setTycoonAssetSelection(index);
});

for (const [selector, action] of [
  ['#tycoonBuildBtn', 'BUILD'],
  ['#tycoonSellBuildingBtn', 'SELL_BUILDING'],
  ['#tycoonMortgageBtn', 'MORTGAGE'],
  ['#tycoonUnmortgageBtn', 'UNMORTGAGE']
]) {
  queryOne(selector).addEventListener('click', () => {
    if (selectedTycoonAsset !== null) performTycoonAction(action, selectedTycoonAsset);
  });
}

queryOne('#tycoonRestartBtn').addEventListener('click', () => {
  if (currentMode === 'online') return;
  const count = Number(queryOne('#tycoonPlayerCount').value) || 2;
  if (tycoonGame && !tycoonGame.finished && tycoonGame.round > 1 && !confirm('Bắt đầu ván mới sẽ thay thế ván đang lưu. Tiếp tục?')) return;
  tycoonGame = tycoonController.createLocal(count, playerIdentity.profile);
  selectedTycoonAsset = null;
  tycoonAvatarCoordinator.resetLocalSelection();
  saveTycoonGame();
  renderTycoon();
});

queryOne('#tycoonChangeModeBtn').addEventListener('click', () => {
  if (currentMode === 'online' && currentRoomCode) {
    requestLeaveRoom('mode');
    return;
  }
  history.replaceState(null, '', location.pathname);
  applyModeCapabilities();
  showView('modePicker');
});

queryOne('#tycoonOnlineResignBtn').addEventListener('click', () => {
  if (currentMode !== 'online' || currentGameType !== 'tycoon') return;
  if (!confirm('Đầu hàng ván Cờ Tỷ Phú LIVE? Bạn sẽ bị loại khỏi ván nhưng vẫn có thể xem tiếp.')) return;
  queryOne('#tycoonOnlineResignBtn').disabled = true;
  sendWsMessage({ type: MSG_TYPES.RESIGN, protocolVersion: PROTOCOL_VERSION, deviceId });
});

queryOne('#tycoonOnlineRematchBtn').addEventListener('click', () => {
  if (currentMode !== 'online' || currentGameType !== 'tycoon') return;
  sendWsMessage({ type: MSG_TYPES.REQUEST_REMATCH, protocolVersion: PROTOCOL_VERSION, deviceId });
});

queryOne('#tycoonOnlineLeaveBtn').addEventListener('click', () => requestLeaveRoom('lobby'));

queryOne('#copyFenBtn').addEventListener('click', async () => {
  if (currentGameType !== 'chess') return;
  try {
    let fen = '';
    if (currentMode === 'online' && onlineState) {
      fen = onlineState.fen || '';
    } else if (localGame) {
      fen = localGame.toFEN();
    }
    await navigator.clipboard.writeText(fen);
    queryOne('#copyFenBtn').textContent = 'Đã sao chép!';
    setTimeout(() => queryOne('#copyFenBtn').textContent = 'Sao chép FEN', 1200);
  } catch {}
});

// Theme Management
initTheme({ button: queryOne('#themeBtn') });

// Prevent Dragging Artifacts
boardEl.addEventListener('dragstart', e => e.preventDefault());

// Keep the home LIVE directory fresh without polling while it is hidden.
const HOME_ROOM_REFRESH_MS = 20_000;
setInterval(() => {
  const homeVisible = !queryOne('#gamePicker')?.classList.contains('hidden');
  if (!document.hidden && homeVisible) {
    void homeRoomDirectoryController?.refresh({ quiet: true });
  }
}, HOME_ROOM_REFRESH_MS);

document.addEventListener('visibilitychange', () => {
  const homeVisible = !queryOne('#gamePicker')?.classList.contains('hidden');
  if (!document.hidden && homeVisible) {
    void homeRoomDirectoryController?.refresh({ quiet: true });
  }
});

// Service Worker Registration
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js').catch(() => {});
  });
}

// Start from an invite link when room and game are present.
const initialParams = new URLSearchParams(location.search);
const initialGame = initialParams.get('game');
const initialRoom = normalizeRoomCode(initialParams.get('room'));
if (['chess', 'xiangqi', 'caro', 'tycoon'].includes(initialGame) && isValidRoomCode(initialRoom)) {
  selectGameType(initialGame);
  currentMode = 'online';
  initOnlineRoomLobby();
} else {
  showView('gamePicker');
}
